/**
 * Tela de visão geral da aplicação.
 * Resumo do que o Nexusprime oferece: envio, receber, carteiras, configurações, notificações e taxas.
 * Modal de doação: envio direto pela aplicação, sem taxa.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ArrowLeft, Send, QrCode, Wallet, Settings, Bell, Coins, Loader2 } from 'lucide-react';
import { FEE_PERCENT, FEE_BILLING_PERCENT, FEE_ADDRESS, IS_FEE_ENABLED, getFeeAddress } from '@/config/feeConfig';
import { STABLECOIN_CONTRACTS } from '@/config/stablecoinContracts';
import { sanitizeAmountInput, parseAmountFromInput, formatAmountForBlur, formatCurrencyDisplay } from '@/utils/currency';
import { sendStablecoinTransferCompletao } from '@/utils/sendStablecoinTransferCompletao';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useToast } from '@/hooks/useToast';
import Modal from '@/components/ui/Modal';
import Button from '../ui/Button';

const DEFAULT_DONATION_AMOUNT_NUM = 10;
const SUGGESTED_AMOUNTS = [5, 10, 20, 50, 100];

export default function AppOverviewScreen({ onBack }) {
  const { t } = useTranslation();
  const { success, error: showError, info } = useToast();
  const {
    provider,
    walletAddress,
    selectedNetwork,
    walletStablecoinBalances,
    connectedWallet,
  } = useWalletConnection();

  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationAmount, setDonationAmount] = useState(() => formatAmountForBlur(DEFAULT_DONATION_AMOUNT_NUM, 'USD'));
  const [donationToken, setDonationToken] = useState('');
  const [isSendingDonation, setIsSendingDonation] = useState(false);

  const feePercent = FEE_PERCENT;
  const feeBillingPercent = FEE_BILLING_PERCENT;
  const hasFeeInfo = IS_FEE_ENABLED && (feePercent > 0 || feeBillingPercent > 0);

  const donationAddressForChain = selectedNetwork?.chainId ? getFeeAddress(selectedNetwork.chainId) : null;
  const canSendFromApp = Boolean(FEE_ADDRESS && donationAddressForChain && walletAddress && provider);

  const amountNum = parseAmountFromInput(donationAmount, 'USD') || 0;

  const donationTokens = useMemo(() => {
    const chainId = selectedNetwork?.chainId;
    const contracts = chainId ? STABLECOIN_CONTRACTS[chainId] : null;
    if (!contracts) return [];
    return Object.keys(contracts).map((symbol) => {
      const balance = walletStablecoinBalances?.[symbol] != null
        ? parseFloat(String(walletStablecoinBalances[symbol]))
        : 0;
      return { symbol, balance };
    });
  }, [selectedNetwork?.chainId, walletStablecoinBalances]);

  const selectedTokenAddress = useMemo(() => {
    if (!donationToken || !selectedNetwork?.chainId) return null;
    const contracts = STABLECOIN_CONTRACTS[selectedNetwork.chainId];
    return contracts?.[donationToken] ?? null;
  }, [donationToken, selectedNetwork?.chainId]);

  const selectedTokenBalance = useMemo(() => {
    const t = donationTokens.find((x) => x.symbol === donationToken);
    return t ? t.balance : 0;
  }, [donationTokens, donationToken]);
  const hasBalance = selectedTokenBalance > 0;

  useEffect(() => {
    if (!showDonationModal) return;
    if (!hasBalance) {
      const zeroStr = formatAmountForBlur(0, 'USD');
      if (donationAmount !== zeroStr) {
        setDonationAmount(zeroStr);
      }
    }
  }, [showDonationModal, hasBalance, donationAmount]);

  const hasDonationTokens = donationTokens.length > 0;
  const stablecoinCards = hasDonationTokens ? (
    <div className="flex flex-wrap gap-2">
      {donationTokens.map(({ symbol, balance }) => {
        const isSelectedToken = donationToken === symbol;
        return (
          <button
            key={symbol}
            type="button"
            onClick={() => setDonationToken(symbol)}
            className={`flex-1 min-w-[7rem] px-3 py-2 rounded-xl border text-sm flex items-center justify-between transition ${
              isSelectedToken
                ? 'border-purple-400 bg-purple-900/40 text-white'
                : 'border-purple-500/40 bg-gray-800/70 text-gray-200 hover:bg-gray-700/70'
            }`}
          >
            <span className="font-semibold">{symbol}</span>
            <span className="text-emerald-400 text-xs">
              {formatCurrencyDisplay(balance || 0, 'USD')}
            </span>
          </button>
        );
      })}
    </div>
  ) : null;

  const canSubmitDonation =
    canSendFromApp &&
    amountNum > 0 &&
    selectedTokenAddress &&
    selectedTokenBalance >= amountNum &&
    !isSendingDonation;

  const handleSendDonation = async () => {
    if (!canSubmitDonation || !provider || !donationAddressForChain) return;
    setIsSendingDonation(true);
    info(t('overview.donationSending'));
    const chainId = selectedNetwork?.chainId;
    try {
      const tx = await sendStablecoinTransferCompletao(
        provider,
        selectedTokenAddress,
        donationAddressForChain,
        String(amountNum),
        connectedWallet === 'walletconnect' ? undefined : chainId,
      );
      const txHash = tx?.hash;
      if (txHash) {
        success(t('overview.donationSuccess'));
      } else {
        showError(t('overview.donationErrorGeneric'));
      }
    } catch (err) {
      const msg = err?.message || err?.code;
      if (msg && (String(msg).includes('reject') || String(msg).includes('denied') || err?.code === 4001)) {
        showError(t('transfer.txCancelled'));
      } else {
        showError(msg || t('overview.donationErrorGeneric'));
      }
    } finally {
      setIsSendingDonation(false);
    }
  };

  const features = [
    { key: 'featureSend', icon: Send },
    { key: 'featureCharge', icon: QrCode },
    { key: 'featureWallets', icon: Wallet },
    { key: 'featureSettings', icon: Settings },
    { key: 'featureNotifications', icon: Bell },
    { key: 'featureFees', icon: Coins },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <button
          type="button"
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
          aria-label={t('overview.backAria')}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-2xl font-bold text-white">{t('overview.title')}</h1>
      </div>

      <div className="space-y-6">
        <p className="text-gray-300 leading-relaxed">
          <Trans
            i18nKey="overview.intro"
            values={{
              feePercent: hasFeeInfo ? feePercent : 0,
              feeBillingPercent: hasFeeInfo ? feeBillingPercent : 0,
            }}
            components={{ strong: <strong className="font-semibold" /> }}
          />
        </p>

        {FEE_ADDRESS && (
          <div>
            <button
              type="button"
              onClick={() => {
                const withBalance = donationTokens.find((t) => t.balance > 0);
                const initialToken = (withBalance || donationTokens[0])?.symbol || '';
                const hasInitialBalance = !!withBalance && withBalance.balance > 0;
                setDonationToken(initialToken);
                setDonationAmount(
                  formatAmountForBlur(hasInitialBalance ? DEFAULT_DONATION_AMOUNT_NUM : 0, 'USD'),
                );
                setShowDonationModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium shadow-sm"
            >
              {t('overview.donationCta')}
            </button>
          </div>
        )}

        <section className="bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6">
          <h2 className="text-white font-medium mb-3">{t('overview.whatIsTitle')}</h2>
          <p className="text-gray-400 text-sm leading-relaxed">{t('overview.whatIsBody')}</p>
        </section>

        <section className="bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6">
          <h2 className="text-white font-medium mb-4">{t('overview.featuresTitle')}</h2>
          <ul className="space-y-4">
            {features.map(({ key, icon: Icon }) => (
              <li key={key} className="flex gap-3">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Icon size={18} className="text-purple-400" />
                </div>
                <p className="text-gray-400 text-sm leading-relaxed pt-1">{t(`overview.${key}`)}</p>
              </li>
            ))}
          </ul>
        </section>

        <p className="text-gray-500 text-sm italic border-l-2 border-purple-500/50 pl-4">
          {t('overview.disclaimer')}
        </p>
      </div>

      {FEE_ADDRESS && (
        <Modal
          isOpen={showDonationModal}
          onClose={() => setShowDonationModal(false)}
          title={t('overview.donationTitle')}
        >
          <div className="space-y-5">
            {!canSendFromApp && (
              <p className="text-sm text-amber-200/90">{t('overview.donationConnectWallet')}</p>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('overview.donationAmountLabel')}
              </label>
              <div
                className={`flex rounded-lg bg-gray-700 ${
                  hasBalance ? 'focus-within:ring-2 focus-within:ring-purple-500' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <span className="flex items-center pl-3 text-gray-400 font-medium">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={donationAmount}
                  onChange={(e) => {
                    if (!hasBalance) return;
                    setDonationAmount(sanitizeAmountInput(e.target.value, 'USD'));
                  }}
                  onBlur={() => {
                    if (!hasBalance) {
                      setDonationAmount('');
                      return;
                    }
                    let n = parseAmountFromInput(donationAmount, 'USD');
                    if (Number.isNaN(n) || n <= 0) {
                      setDonationAmount('');
                      return;
                    }
                    if (n > selectedTokenBalance) {
                      n = selectedTokenBalance;
                    }
                    setDonationAmount(formatAmountForBlur(n, 'USD'));
                  }}
                  placeholder="0.00"
                  className={`w-full bg-transparent text-white py-2.5 pr-3 pl-1 placeholder-gray-500 focus:outline-none ${
                    !hasBalance ? 'cursor-not-allowed' : ''
                  }`}
                  disabled={!hasBalance}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-between">
              {SUGGESTED_AMOUNTS.map((v) => {
                const current = parseAmountFromInput(donationAmount, 'USD');
                const isSelected = !Number.isNaN(current) && current === v;
                const disabledSuggestion = !hasBalance;
                return (
                  <button
                    key={v}
                    type="button"
                    className={`min-w-[3rem] flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                      disabledSuggestion
                        ? 'bg-gray-800 text-gray-500 border border-gray-700 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700/80 text-gray-300 hover:bg-gray-600 border border-gray-600'
                    }`}
                    disabled={disabledSuggestion}
                    onClick={() => {
                      if (!hasBalance) return;
                      const max = selectedTokenBalance;
                      const effective = Math.min(v, max);
                      setDonationAmount(formatAmountForBlur(effective, 'USD'));
                    }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>

            {stablecoinCards}

            <Button
              type="button"
              variant="primary"
              className="w-full py-3 rounded-xl text-base font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 transition-shadow flex items-center justify-center gap-2"
              disabled={!canSubmitDonation}
              onClick={handleSendDonation}
            >
              {isSendingDonation ? (
                <Loader2 size={20} className="animate-spin shrink-0" />
              ) : (
                <Send size={20} className="shrink-0" />
              )}
              <span>{t('overview.donationSend')}</span>
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
